import { type CatalogItem, type DevicePlaces, type DeviceView, type LobbySignage, type LobbySpecial, type SignageArtSlot, type WebsiteFeed, SIGNAGE_ART_LABELS, SIGNAGE_ART_SLOTS } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { del, errorMessage, get, patch, post, put } from "../../api";
import { usePlans } from "../Plans";
import { Alert, Badge, Button, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, formatDateTime } from "../../components/ui";
import type { SettingsResponse } from "./common";

/** Clinic devices (iPads, the lobby screen): add one, pair it with a code or link, and unlink it if it's lost. */
export function DevicesTab({ data }: { data: SettingsResponse }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["devices"], queryFn: () => get<DeviceView[]>("/api/devices"), refetchInterval: 10_000 });
  const [name, setName] = useState("");
  const set = (d: DeviceView[]) => qc.setQueryData(["devices"], d);
  const add = useMutation({ mutationFn: () => post<DeviceView[]>("/api/devices", { name }), onSuccess: (d) => (set(d), setName("")) });
  const code = useMutation({ mutationFn: (id: string) => post<DeviceView[]>(`/api/devices/${id}/code`, {}), onSuccess: set });
  const unlink = useMutation({ mutationFn: (id: string) => del<DeviceView[]>(`/api/devices/${id}`), onSuccess: set });
  const places = useQuery({ queryKey: ["device-places"], queryFn: () => get<DevicePlaces>("/api/devices/places") });
  const place = useMutation({
    mutationFn: (v: { id: string; locationId: string | null; eventOccurrenceId: string | null }) => put<DeviceView[]>(`/api/devices/${v.id}/place`, { locationId: v.locationId, eventOccurrenceId: v.eventOccurrenceId }),
    onSuccess: set,
  });
  const walkIns = useMutation({ mutationFn: (v: { id: string; on: boolean }) => put<DeviceView[]>(`/api/devices/${v.id}/walk-ins`, { allowWalkIns: v.on }), onSuccess: set });
  const lobby = useMutation({ mutationFn: (v: { id: string; on: boolean }) => put<DeviceView[]>(`/api/devices/${v.id}/lobby`, { lobby: v.on }), onSuccess: set });
  // A one-time pairing link (for a device set up remotely: paste it as the kiosk app's start page).
  const [links, setLinks] = useState<Record<string, string>>({});
  const makeLink = useMutation({
    mutationFn: (id: string) => post<{ link: string }>(`/api/devices/${id}/link`, {}),
    onSuccess: (r, id) => (setLinks((l) => ({ ...l, [id]: r.link })), void qc.invalidateQueries({ queryKey: ["devices"] })),
  });
  const screenings = useMutation({ mutationFn: (v: { id: string; on: boolean }) => put<DeviceView[]>(`/api/devices/${v.id}/screenings`, { allowScreenings: v.on }), onSuccess: set });
  const slug = data.clinic.slug;
  const pageUrl = slug ? `${window.location.origin}/device/${slug}` : null;

  return (
    <div className="flex flex-col gap-6">
      <LobbyPanel data={data} />
      <SignagePanel data={data} />
      <Panel>
        <PanelHeader
          title="Clinic devices"
          description="iPads and the lobby screen. Clients check themselves in on one (last name and date of birth), then fill out any missing forms and start their video exam there. The front desk can also send a visit's forms to one. They don't sign in as anyone and can't see anything else. Mobile visits don't use them."
        />
        <div className="flex flex-col gap-2 px-5 py-4 text-sm text-ink-soft">
          <p>
            <b>To set one up:</b> add it below, then on the device open {pageUrl ? <b className="break-all text-ink">{pageUrl}</b> : "the device page (set a link name under Clinic first)"} and type its code (or use a pairing link: paste it as the start page in a kiosk app). Add the page to the home screen and leave it open.
          </p>
        </div>
        {list.isLoading ? (
          <Loading />
        ) : (
          <ul className="divide-y divide-mist border-t border-line">
            {list.data?.length === 0 && <li className="px-5 py-4 text-sm text-muted">No devices yet.</li>}
            {list.data?.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div>
                  <div className="flex items-center gap-2 font-semibold">
                    {d.name}
                    {d.paired ? d.online ? <Badge tone="ok">Online</Badge> : <Badge tone="off">Offline</Badge> : <Badge tone="wait">Not set up</Badge>}
                    {d.busyWith && <Badge tone="brand">Showing {d.busyWith.clientName}'s forms</Badge>}
                  </div>
                  {d.paired && (
                    <label className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-soft">
                      Used at
                      <select
                        aria-label={`Where ${d.name} is used`}
                        className="rounded border border-line bg-white px-2 py-1"
                        value={d.eventOccurrenceId && d.placeLabel.startsWith("Event") ? `e:${d.eventOccurrenceId}` : d.locationId ? `l:${d.locationId}` : ""}
                        onChange={(e) => {
                          const [k, id] = e.target.value.split(":");
                          place.mutate({ id: d.id, locationId: k === "l" ? id! : null, eventOccurrenceId: k === "e" ? id! : null });
                        }}
                      >
                        <option value="">Clinic (any location)</option>
                        {places.data?.locations.map((l) => (
                          <option key={l.id} value={`l:${l.id}`}>
                            {l.name}
                          </option>
                        ))}
                        {places.data?.events.map((o) => (
                          <option key={o.id} value={`e:${o.id}`}>
                            Event: {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-muted">An event lasts until 12 hours after it ends; then it's back to the clinic.</span>
                    </label>
                  )}
                  <label className="mt-1 flex items-center gap-2 text-sm text-ink-soft">
                    <input type="checkbox" checked={d.lobby} onChange={(e) => lobby.mutate({ id: d.id, on: e.target.checked })} />
                    Lobby display (Check in, Walk in, and Book on its welcome; a screen saver; every screen cleared after a minute untouched)
                  </label>
                  {d.paired && (
                    <label className="flex items-center gap-2 text-sm text-ink-soft">
                      <input type="checkbox" checked={d.allowWalkIns} onChange={(e) => walkIns.mutate({ id: d.id, on: e.target.checked })} />
                      Allow walk-ins (clients book themselves in for the first open time)
                    </label>
                  )}
                  {d.paired && (
                    <label className="flex items-center gap-2 text-sm text-ink-soft">
                      <input type="checkbox" checked={d.allowScreenings} onChange={(e) => screenings.mutate({ id: d.id, on: e.target.checked })} />
                      Telehealth screenings on this device (off: the link is sent to the client's phone)
                    </label>
                  )}
                  <div className="text-sm text-muted">
                    {links[d.id] ? (
                      <span className="flex flex-col gap-1">
                        <span>One-time pairing link (good for 24 hours; it stops working once the device opens it). Paste it as the start page in the kiosk app:</span>
                        <span className="flex items-center gap-2">
                          <input readOnly value={links[d.id]} onFocus={(e) => e.target.select()} className="w-full rounded border border-line px-2 py-1 font-mono text-xs text-ink" />
                          <Button variant="quiet" onClick={() => void navigator.clipboard?.writeText(links[d.id]!)}>
                            Copy
                          </Button>
                        </span>
                      </span>
                    ) : d.pairCode && d.pairCode.length > 6 ? (
                      <>Pairing link waiting (good until {formatDateTime(d.pairExpiresAt!)}). Make a new one to see it again.</>
                    ) : d.pairCode ? (
                      <>
                        Code: <b className="font-mono text-lg tracking-[0.2em] text-ink">{d.pairCode}</b> (good until {formatDateTime(d.pairExpiresAt!)})
                      </>
                    ) : d.paired ? (
                      d.lastSeenAt ? `Last seen ${formatDateTime(d.lastSeenAt)}` : "Paired"
                    ) : (
                      "The code ran out: make a new one."
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="quiet" busy={code.isPending && code.variables === d.id} onClick={() => (!d.paired || window.confirm(`Make a new code for ${d.name}? It will need setting up again.`)) && code.mutate(d.id)}>
                    {d.paired ? "Set up again" : "New code"}
                  </Button>
                  <Button variant="quiet" busy={makeLink.isPending && makeLink.variables === d.id} onClick={() => (!d.paired || window.confirm(`Make a pairing link for ${d.name}? It will need setting up again.`)) && makeLink.mutate(d.id)}>
                    Pairing link
                  </Button>
                  <Button variant="quiet" className="text-danger" onClick={() => window.confirm(`Unlink ${d.name}? It stops working right away.`) && unlink.mutate(d.id)}>
                    Unlink
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <form
          className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <Field label="Add a device" placeholder="Check-in iPad or Lobby screen" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" variant="primary" busy={add.isPending} disabled={!name.trim()}>
            Add and get a code
          </Button>
        </form>
        {(add.error || code.error || unlink.error || place.error || walkIns.error || screenings.error) && (
          <div className="px-5 pb-4">
            <Alert>{errorMessage(add.error ?? code.error ?? unlink.error ?? place.error ?? walkIns.error ?? screenings.error)}</Alert>
          </div>
        )}
      </Panel>
    </div>
  );
}

/** The lobby display's screen saver video and its specials (each can open a plan's sign-up or a service's booking). */
function LobbyPanel({ data }: { data: SettingsResponse }) {
  const qc = useQueryClient();
  const slug = data.clinic.slug ?? "";
  const videoKey = data.settings["lobby.videoKey"];
  const [specials, setSpecials] = useState<LobbySpecial[]>(data.settings["lobby.specials"]);
  const [notice, setNotice] = useState<string | null>(null);
  const plans = usePlans();
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: () => get<{ items: CatalogItem[] }>("/api/catalog") });
  const services = (catalog.data?.items ?? []).filter((i) => i.type === "service" && i.status === "active");
  const refresh = (msg: string) => (void qc.invalidateQueries({ queryKey: ["settings"] }), setNotice(msg));
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 200 * 1024 * 1024) throw new Error("Keep the video under 200 MB.");
      const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/devices/lobby-video-upload", { contentType: file.type });
      const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
      if (!res.ok) throw new Error("The video couldn't be uploaded. Try again.");
      await patch("/api/settings", { "lobby.videoKey": up.key });
    },
    onSuccess: () => refresh("Video saved. The lobby display plays it from its next screen saver."),
  });
  const removeVideo = useMutation({ mutationFn: () => patch("/api/settings", { "lobby.videoKey": null }), onSuccess: () => refresh("Video removed.") });
  const save = useMutation({ mutationFn: () => patch("/api/settings", { "lobby.specials": specials }), onSuccess: () => refresh("Specials saved.") });
  const edit = (i: number, v: Partial<LobbySpecial>) => (setNotice(null), setSpecials((cur) => cur.map((x, j) => (j === i ? { ...x, ...v } : x))));
  const linkValue = (x: LobbySpecial) => (x.link ? `${x.link.kind}:${x.link.id}` : "");
  const error = upload.error ?? removeVideo.error ?? save.error;
  return (
    <Panel>
      <PanelHeader title="Lobby display" description="The screen saver video and the specials on any device set as a lobby display." />
      <div className="flex flex-col gap-5 p-5">
        {notice && <Alert tone="ok">{notice}</Alert>}
        {error && <Alert>{errorMessage(error)}</Alert>}
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Screen saver video</h3>
          {videoKey ? <video src={`/api/book/${slug}/lobby-video?v=${encodeURIComponent(videoKey.split("/").pop() ?? "")}`} muted loop autoPlay playsInline className="h-64 w-36 rounded border border-line bg-ink object-cover" /> : <p className="text-sm text-muted">No video: the screen saver shows the clinic's name and the next opening.</p>}
          <div className="flex flex-wrap items-center gap-3">
            <label className="cursor-pointer text-sm font-semibold text-brand">
              {upload.isPending ? "Uploading…" : videoKey ? "Replace video" : "Upload a video"}
              <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" disabled={upload.isPending} onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} />
            </label>
            {videoKey && (
              <Button variant="quiet" className="text-danger" busy={removeVideo.isPending} onClick={() => removeVideo.mutate()}>
                Remove video
              </Button>
            )}
          </div>
          <p className="text-xs text-muted">Portrait (tall) MP4 works best. It plays muted, on a loop.</p>
        </div>
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold">Specials</h3>
          {!specials.length && <p className="text-sm text-muted">No specials yet.</p>}
          {specials.map((x, i) => (
            <div key={x.id} className="grid gap-2 rounded border border-line p-3 sm:grid-cols-2">
              <Field label="Ribbon (short)" placeholder="✨ BOGO DEAL" maxLength={40} value={x.ribbon} onChange={(e) => edit(i, { ribbon: e.target.value })} />
              <Field label="Headline" maxLength={120} value={x.headline} onChange={(e) => edit(i, { headline: e.target.value })} />
              <TextAreaField label="Line under it (optional)" maxLength={300} value={x.text} onChange={(e) => edit(i, { text: e.target.value })} className="sm:col-span-2" />
              <SelectField
                label="Tapping it opens"
                value={linkValue(x)}
                onChange={(e) => {
                  const [kind, id] = e.target.value.split(":");
                  edit(i, { link: kind && id ? { kind: kind as "plan" | "service", id } : null });
                }}
              >
                <option value="">Nothing (just shown)</option>
                <optgroup label="Plans (sign-up page)">
                  {(plans.data ?? []).filter((p) => p.status === "active").map((p) => (
                    <option key={p.id} value={`plan:${p.id}`}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Services (booking with it picked)">
                  {services.map((sv) => (
                    <option key={sv.id} value={`service:${sv.id}`}>
                      {sv.name}
                    </option>
                  ))}
                </optgroup>
              </SelectField>
              <div className="flex items-end justify-between gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={x.active} onChange={(e) => edit(i, { active: e.target.checked })} />
                  Showing
                </label>
                <Button variant="quiet" className="text-danger" onClick={() => setSpecials((cur) => cur.filter((_, j) => j !== i))}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <div className="flex gap-3">
            <Button variant="quiet" disabled={specials.length >= 10} onClick={() => setSpecials((cur) => [...cur, { id: crypto.randomUUID().slice(0, 12), ribbon: "", headline: "", text: "", link: null, active: true }])}>
              Add a special
            </Button>
            <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
              Save specials
            </Button>
          </div>
        </div>
      </div>
    </Panel>
  );
}

/** Where each picture is used, for the artwork list (docs/lobby-signage.md). */
const artSrc = (slug: string, slot: string, key: string) => `/api/book/${slug}/lobby-art/${slot}?v=${encodeURIComponent(key.split("/").pop() ?? "")}`;

/**
 * The lobby's animated menu: on or off, the services featured on their own (with a picture each),
 * what's left off the menu boards, each board's picture, and the artwork. Prices, names, and
 * descriptions come from the catalog; nothing is typed in here.
 */
function SignagePanel({ data }: { data: SettingsResponse }) {
  const qc = useQueryClient();
  const slug = data.clinic.slug ?? "";
  const saved = data.settings["lobby.signage"];
  const [v, setV] = useState<LobbySignage>(saved);
  const [notice, setNotice] = useState<string | null>(null);
  const feed = useQuery({ queryKey: ["signage-feed", slug], queryFn: () => get<WebsiteFeed>(`/v1/public/clinics/${slug}/website`), enabled: !!slug });
  const services = (feed.data?.services ?? []).filter((x) => x.inClinic);
  const artOptions = SIGNAGE_ART_SLOTS.filter((x) => x !== "logo" && x !== "logo-mark");
  const set = (p: Partial<LobbySignage>) => (setNotice(null), setV((cur) => ({ ...cur, ...p })));
  const refresh = (msg: string) => (void qc.invalidateQueries({ queryKey: ["settings"] }), setNotice(msg));
  const save = useMutation({ mutationFn: () => patch("/api/settings", { "lobby.signage": v }), onSuccess: () => refresh("Saved. The lobby display picks it up within a minute.") });
  // Pictures save on their own (on top of what's saved, so unsaved picks above aren't lost or saved early).
  // (The latest saved pictures, so two uploads in a row both stick before the settings reload.)
  const savedArt = useRef(saved.art);
  const saveArt = async (art: LobbySignage["art"]) => {
    await patch("/api/settings", { "lobby.signage": { ...saved, art } });
    savedArt.current = art;
    setV((cur) => ({ ...cur, art }));
  };
  /** Store one picture; returns its key (saved by the caller). */
  const store = async (slot: SignageArtSlot, file: File) => {
    if (file.size > 15 * 1024 * 1024) throw new Error(`Keep each picture under 15 MB (${file.name}).`);
    const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/devices/lobby-art-upload", { slot, contentType: file.type });
    const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
    if (!res.ok) throw new Error(`${file.name} couldn't be uploaded. Try again.`);
    return up.key;
  };
  const upload = useMutation({
    mutationFn: async ({ slot, file }: { slot: SignageArtSlot; file: File }) => saveArt({ ...savedArt.current, [slot]: await store(slot, file) }),
    onSuccess: (_r, x) => refresh(`${SIGNAGE_ART_LABELS[x.slot]} saved.`),
  });
  // Several at once: each file goes in the slot its name matches (brand-bg.png → Intro background).
  const uploadMany = useMutation({
    mutationFn: async (files: File[]) => {
      const slotOf = (name: string) => name.replace(/\.[^.]+$/, "").trim().toLowerCase() as SignageArtSlot;
      const matched = files.filter((f) => SIGNAGE_ART_SLOTS.includes(slotOf(f.name)));
      const skipped = files.filter((f) => !matched.includes(f)).map((f) => f.name);
      const art = { ...savedArt.current };
      for (const f of matched) art[slotOf(f.name)] = await store(slotOf(f.name), f);
      if (matched.length) await saveArt(art);
      return { saved: matched.length, skipped };
    },
    onSuccess: (r) => refresh(`${r.saved} picture${r.saved === 1 ? "" : "s"} saved.${r.skipped.length ? ` Not matched to a slot (rename to the slot's name): ${r.skipped.join(", ")}.` : ""}`),
  });
  const removeArt = useMutation({
    mutationFn: async (slot: SignageArtSlot) => {
      const art = { ...savedArt.current };
      delete art[slot];
      await saveArt(art);
    },
    onSuccess: () => refresh("Picture removed."),
  });
  const move = (i: number, by: number) => {
    const f = [...v.featured];
    const [x] = f.splice(i, 1);
    f.splice(Math.max(0, Math.min(f.length, i + by)), 0, x!);
    set({ featured: f });
  };
  const error = save.error ?? upload.error ?? uploadMany.error ?? removeArt.error;
  return (
    <Panel>
      <PanelHeader title="Lobby display: animated menu" description="The screen saver plays your services, prices, memberships, and specials, live from the catalog. Tap the screen to check in or book." />
      <div className="flex flex-col gap-6 p-5">
        {notice && <Alert tone="ok">{notice}</Alert>}
        {error && <Alert>{errorMessage(error)}</Alert>}
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={v.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
          Play the animated menu as the screen saver (off: the video and specials, as before)
        </label>

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Featured (each gets its own scene; three per loop, taking turns)</h3>
          {!v.featured.length && <p className="text-sm text-muted">None yet: the loop goes straight to the menu boards.</p>}
          {v.featured.map((f, i) => (
            <div key={`${f.serviceId}-${i}`} className="flex flex-wrap items-end gap-2">
              <SelectField label={`#${i + 1}`} value={f.serviceId} onChange={(e) => set({ featured: v.featured.map((x, j) => (j === i ? { ...x, serviceId: e.target.value } : x)) })}>
                {services.map((sv) => (
                  <option key={sv.id} value={sv.id}>
                    {sv.name}
                  </option>
                ))}
              </SelectField>
              <SelectField label="Picture (if it has no photo)" value={f.art ?? ""} onChange={(e) => set({ featured: v.featured.map((x, j) => (j === i ? { ...x, art: (e.target.value || null) as SignageArtSlot | null } : x)) })}>
                <option value="">Animated gradient</option>
                {artOptions.map((a) => (
                  <option key={a} value={a}>
                    {SIGNAGE_ART_LABELS[a]}
                  </option>
                ))}
              </SelectField>
              <Button variant="quiet" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                ↑
              </Button>
              <Button variant="quiet" disabled={i === v.featured.length - 1} onClick={() => move(i, 1)} aria-label="Move down">
                ↓
              </Button>
              <Button variant="quiet" className="text-danger" onClick={() => set({ featured: v.featured.filter((_, j) => j !== i) })}>
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button variant="quiet" disabled={!services.length || v.featured.length >= 20} onClick={() => set({ featured: [...v.featured, { serviceId: services[0]!.id, art: null }] })}>
              Add a featured service
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Menu boards (one per category)</h3>
          {(feed.data?.categories ?? []).map((c) => {
            const rows = services.filter((x) => x.category === c.name);
            if (!rows.length) return null;
            return (
              <div key={c.name} className="rounded border border-line p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <span className="font-semibold">{c.name}</span>
                  <SelectField label="Picture beside the list" value={v.boardArt[c.name] ?? ""} onChange={(e) => set({ boardArt: { ...v.boardArt, [c.name]: (e.target.value || null) as SignageArtSlot | null } })}>
                    <option value="">None</option>
                    {artOptions.map((a) => (
                      <option key={a} value={a}>
                        {SIGNAGE_ART_LABELS[a]}
                      </option>
                    ))}
                  </SelectField>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
                  {rows.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!v.hidden.includes(r.id)} onChange={(e) => set({ hidden: e.target.checked ? v.hidden.filter((h) => h !== r.id) : [...v.hidden, r.id] })} />
                      {r.name}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted">Untick anything that shouldn't be advertised on the screen (prescription medicines like Toradol or Zofran, for instance). Names, descriptions, and prices come from the catalog.</p>
        </div>

        <div>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save animated menu
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Artwork</h3>
          <p className="text-xs text-muted">Portrait pictures (9:16) with no text in them; transparent PNGs where noted. Each saves as soon as it's uploaded. Anything missing shows an animated gradient.</p>
          <div>
            <label className="inline-flex cursor-pointer items-center rounded-[6px] border border-line px-3 py-2 text-sm font-semibold text-brand">
              {uploadMany.isPending ? "Uploading…" : "Upload several"}
              <input type="file" multiple accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" disabled={uploadMany.isPending} onChange={(e) => e.target.files?.length && uploadMany.mutate([...e.target.files])} />
            </label>
            <span className="ml-3 text-xs text-muted">Each file goes in the slot named like it: brand-bg.png → Intro background, logo.png → Logo, and so on (the name under each box).</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {SIGNAGE_ART_SLOTS.map((slot) => {
              const key = saved.art[slot];
              const busy = (upload.isPending && upload.variables?.slot === slot) || (removeArt.isPending && removeArt.variables === slot);
              return (
                <div key={slot} className="flex flex-col gap-1 rounded border border-line p-2">
                  <div className="flex h-36 items-center justify-center overflow-hidden rounded bg-ink">{key ? <img src={artSrc(slug, slot, key)} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-white/60">{slot}</span>}</div>
                  <span className="text-xs font-semibold">{SIGNAGE_ART_LABELS[slot]}</span>
                  <span className="font-mono text-[11px] text-muted">{slot}</span>
                  <div className="flex items-center gap-2 text-xs">
                    <label className="cursor-pointer font-semibold text-brand">
                      {busy ? "Saving…" : key ? "Replace" : "Upload"}
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" disabled={busy} onChange={(e) => e.target.files?.[0] && upload.mutate({ slot, file: e.target.files[0] })} />
                    </label>
                    {key && (
                      <button type="button" className="text-danger" disabled={busy} onClick={() => removeArt.mutate(slot)}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Panel>
  );
}
